import { DEFAULT_LABEL_STYLE, LabelStyle, LabelVariant } from '../../../store/types';

export const LABEL_VARIANT_OPTIONS: { value: LabelVariant; label: string }[] = [
  { value: 'rectangle', label: 'Rectangle' },
  { value: 'pill', label: 'Pill' },
  { value: 'tag', label: 'Tag' },
  { value: 'outline', label: 'Outline' },
  { value: 'plaque', label: 'Plaque' },
  { value: 'neon', label: 'Neon panel' },
];

const DEFAULT_LABEL_VARIANT: LabelVariant = 'rectangle';

export function resolveLabelVariant(variant?: LabelVariant): LabelVariant {
  return variant ?? DEFAULT_LABEL_VARIANT;
}

export function getLabelBadgeClassName(variant?: LabelVariant, isSelected = false): string {
  return [
    'label-badge',
    `label-badge--${resolveLabelVariant(variant)}`,
    isSelected ? 'label-badge--selected' : '',
  ].filter(Boolean).join(' ');
}

const NEON_ACCENT_COLOR = '#49f0ff';

/** Colour of neon overlays (fill, border and glow): their background colour, cyan by default */
export function neonColor(style: LabelStyle) {
  return style.backgroundColor ?? NEON_ACCENT_COLOR;
}

/**
  * Colour of neon label badges (panel, border and glow): their "label colour",
  * stored as border colour, or the overlay one
  */
export function neonLabelColor(style: LabelStyle) {
  const borderColor = style.borderColor ?? DEFAULT_LABEL_STYLE.borderColor;
  return borderColor === 'transparent' ? neonColor(style) : borderColor;
}

// Neon labels and overlays are a light tint of their glow colour by default
export const NEON_DEFAULT_OPACITY = 0.14;

/** Background colour and opacity of a label badge, also the fill of its overlay */
export function getLabelBackground(style: LabelStyle): { color: string; opacity: number } {
  const variant = resolveLabelVariant(style.variant);
  const backgroundOpacity = style.backgroundOpacity ?? DEFAULT_LABEL_STYLE.backgroundOpacity;

  if (variant === 'neon') {
    return { color: neonColor(style), opacity: style.backgroundOpacity ?? NEON_DEFAULT_OPACITY };
  }

  return {
    color: style.backgroundColor ?? DEFAULT_LABEL_STYLE.backgroundColor,
    opacity: variant === 'outline' ? Math.min(backgroundOpacity, 0.14) : backgroundOpacity,
  };
}

export function getLabelBadgeStyleVars(style: LabelStyle, rotation = 0, isSelected = false): Record<string, string> {
  const variant = resolveLabelVariant(style.variant);
  const fontSize = style.fontSize ?? DEFAULT_LABEL_STYLE.fontSize;
  const fontWeight = style.fontWeight ?? DEFAULT_LABEL_STYLE.fontWeight;
  const color = style.color ?? DEFAULT_LABEL_STYLE.color;
  const borderColor = style.borderColor ?? DEFAULT_LABEL_STYLE.borderColor;
  const padding = style.padding ?? DEFAULT_LABEL_STYLE.padding;

  const labelBackground = getLabelBackground(style);
  const glowColor = neonLabelColor(style);
  // Neon badges are tinted with their label colour (same opacity as their overlay)
  const background = hexToRgba(variant === 'neon' ? glowColor : labelBackground.color, labelBackground.opacity);
  // Without border colour, outline labels use their text colour. Neon ones use their label colour
  const resolvedBorderColor = variant === 'neon'
    ? glowColor
    : borderColor === 'transparent' ? (variant === 'outline' ? color : 'transparent') : borderColor;
  const shadow = variant === 'neon'
    ? `0 0 6px ${glowColor}, 0 0 18px ${glowColor}`
    : variant === 'plaque'
    ? '0 1px 0 rgba(255,255,255,0.16) inset, 0 6px 14px rgba(0,0,0,0.22)'
    : variant === 'outline'
      ? '0 1px 2px rgba(0,0,0,0.12)'
      : '0 1px 3px rgba(0,0,0,0.3)';

  return {
    '--label-font-size': `${fontSize}px`,
    '--label-font-weight': fontWeight,
    '--label-text-color': color,
    '--label-background': background,
    '--label-border-color': resolvedBorderColor,
    '--label-border-width': `${variant === 'outline' ? 2 : 1}px`,
    '--label-padding-y': `${padding}px`,
    '--label-padding-x': `${padding + 2}px`,
    '--label-rotation': `${rotation}deg`,
    '--label-skew-x': `${style.skewX ?? 0}deg`,
    '--label-skew-y': `${style.skewY ?? 0}deg`,
    '--label-perspective-x': `${style.perspectiveX ?? 0}deg`,
    '--label-perspective-y': `${style.perspectiveY ?? 0}deg`,
    '--label-shadow': shadow,
    '--label-outline-width': isSelected ? '2px' : '0px',
    '--label-outline-color': isSelected ? '#1a73e8' : 'transparent',
    '--label-outline-offset': isSelected ? '2px' : '0px',
    '--label-neon-accent': glowColor,
  };
}

export function labelBadgeStyleVarsToString(vars: Record<string, string>): string {
  return Object.entries(vars)
    .map(([key, value]) => `${key}:${value}`)
    .join(';');
}

// Map zoom factor (2^zoom) set on the map container, updated as soon as a zoom
// animation starts, so labels scaling with the map zoom along
export const MAP_ZOOM_FACTOR_VAR = '--map-zoom-factor';

/** CSS scale of a label shown at its size on `scaleZoom`, zooming with the map */
export function labelScaleWithMap(scaleZoom: number) {
  const factor = 2 ** scaleZoom;
  return `calc(var(${MAP_ZOOM_FACTOR_VAR}, ${factor}) / ${factor})`;
}

function hexToRgba(hex: string, opacity: number): string {
  if (!hex || !hex.startsWith('#')) return `rgba(51,51,51,${opacity})`;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${opacity})`;
}
